/**
 * Main Application Entry Point
 * Integrates all providers, routing, and core features
 */

import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './providers/NotificationProvider';
import { AdminLayout } from './components/layout/AdminLayout';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { UserManagementPage } from './features/admin/users';
import { FeatureTrackingPage } from './features/admin/feature-tracking';
import { PackageManagement } from './pages/admin/PackageManagement';
import { PackageCapabilityManagement } from './pages/admin/PackageCapabilityManagement';
import { GenerateVideoJobsPage } from './features/admin/generate-video/pages/GenerateVideoJobsPage';
import { TokenPackManagement } from './pages/admin/TokenPackManagement';
import { LanguageSettings } from './pages/admin/LanguageSettings';
import { AnonymousLimits } from './pages/admin/AnonymousLimits';
import { GlobalSettings } from './pages/admin/GlobalSettings';
import { MarketplaceManagement } from './pages/admin/MarketplaceManagement';
import { CapabilityManagement } from './pages/admin/CapabilityManagement';
import { CapabilityDetail } from './pages/admin/CapabilityDetail';
import { InternalAITaskManagement } from './pages/admin/InternalAITaskManagement';
import { InternalAITaskDetail } from './pages/admin/InternalAITaskDetail';
import { LLMCatalogManagement } from './pages/admin/LLMCatalogManagement';
import { InputFormatsManagement } from './pages/admin/InputFormatsManagement';
import { SystemCharacteristicList } from './pages/admin/SystemCharacteristicList';
import { SystemCharacteristicDetail } from './pages/admin/SystemCharacteristicDetail';
import { SystemPromptManagement } from './pages/admin/SystemPromptManagement';
import { AccountSettingsPage } from './pages/admin/AccountSettingsPage';
import LoginPage from './pages/admin/LoginPage';
import { AgentAvatarPage } from './features/admin/agent-avatars';
import { AdminAgentManagementPage } from './features/admin/agents';
import { FeedbackManagementPage } from './features/admin/feedback/pages/FeedbackManagementPage';
import {
  NotificationCampaignDetailPage,
  NotificationCampaignListPage,
  NotificationComposerPage,
  NotificationSuppressionsPage,
} from './features/admin/notifications';
import { CampaignDetailPage, CampaignListPage } from './features/admin/campaigns';
import { PaymentListPage } from './features/admin/payments';
import { ReferralListPage } from './features/admin/referrals';
import { CodeAgentSessionsPage } from './pages/admin/CodeAgentSessionsPage';
import { CodeAgentAuditPage } from './pages/admin/CodeAgentAuditPage';
import { AdminAgentHistoryPage } from './features/admin/admin-agent/pages/AdminAgentHistoryPage';
import { CodeAgentKillSwitchesPage } from './pages/admin/CodeAgentKillSwitchesPage';
import { CodeAgentRuntimeSettingsPage } from './pages/admin/CodeAgentRuntimeSettingsPage';
import { CodeAgentToolsPage } from './pages/admin/CodeAgentToolsPage';
import { ToolCallsManagementPage } from './features/admin/tool-calls';
import { AiLogsPage } from './pages/admin/ai-logs/AiLogsPage';
import { BillingCostReportPage } from './pages/admin/BillingCostReportPage';

const DocsPagesListPage = React.lazy(() =>
  import('./features/admin/docs').then((module) => ({ default: module.DocsPagesListPage }))
);
const DocsPageEditorPage = React.lazy(() =>
  import('./features/admin/docs').then((module) => ({ default: module.DocsPageEditorPage }))
);
const DocsCategoriesPage = React.lazy(() =>
  import('./features/admin/docs').then((module) => ({ default: module.DocsCategoriesPage }))
);
const DocsVideosPage = React.lazy(() =>
  import('./features/admin/docs').then((module) => ({ default: module.DocsVideosPage }))
);
const DocsReleasesPage = React.lazy(() =>
  import('./features/admin/docs').then((module) => ({ default: module.DocsReleasesPage }))
);

// ============================================
// Root Route Component (handles authentication redirect)
// ============================================

const RootRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show nothing while checking authentication status
  if (isLoading) {
    return null;
  }

  // If authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // If not authenticated, show login page
  return <LoginPage />;
};

// ============================================
// Main App Component (with contexts)
// ============================================

const AppContent: React.FC = () => {
  return (
    <Routes>
      {/* Root route - shows login if not authenticated, redirects to dashboard if authenticated */}
      <Route path="/" element={<RootRoute />} />

      {/* Admin Routes - Protected with AdminGuard inside AdminLayout */}
      <Route
        path="/*"
        element={
          <AdminLayout>
            <Routes>
              {/* Main Section */}
              <Route path="/dashboard" element={<AdminDashboard />} />
              <Route path="/users" element={<UserManagementPage />} />
              <Route path="/feedback" element={<FeedbackManagementPage />} />
              <Route path="/admin-mail" element={<NotificationCampaignListPage />} />
              <Route path="/admin-mail/new" element={<NotificationComposerPage />} />
              <Route path="/admin-mail/suppressions" element={<NotificationSuppressionsPage />} />
              <Route path="/admin-mail/:publicId/edit" element={<NotificationComposerPage />} />
              <Route path="/admin-mail/:publicId" element={<NotificationCampaignDetailPage />} />
              <Route
                path="/docs"
                element={
                  <Suspense fallback={null}>
                    <DocsPagesListPage />
                  </Suspense>
                }
              />
              <Route
                path="/docs/pages/new"
                element={
                  <Suspense fallback={null}>
                    <DocsPageEditorPage />
                  </Suspense>
                }
              />
              <Route
                path="/docs/pages/:publicId"
                element={
                  <Suspense fallback={null}>
                    <DocsPageEditorPage />
                  </Suspense>
                }
              />
              <Route
                path="/docs/categories"
                element={
                  <Suspense fallback={null}>
                    <DocsCategoriesPage />
                  </Suspense>
                }
              />
              <Route
                path="/docs/videos"
                element={
                  <Suspense fallback={null}>
                    <DocsVideosPage />
                  </Suspense>
                }
              />
              <Route
                path="/docs/releases"
                element={
                  <Suspense fallback={null}>
                    <DocsReleasesPage />
                  </Suspense>
                }
              />
              <Route path="/admin-agent" element={<AdminAgentManagementPage />} />
              <Route path="/packages" element={<PackageManagement />} />
              <Route path="/package-capabilities" element={<PackageCapabilityManagement />} />
              <Route path="/advance-tokens" element={<TokenPackManagement />} />
              <Route path="/payments" element={<PaymentListPage />} />
              <Route path="/referrals" element={<ReferralListPage />} />
              <Route path="/campaigns" element={<CampaignListPage />} />
              <Route path="/campaigns/:publicId" element={<CampaignDetailPage />} />
              <Route path="/generate-video" element={<GenerateVideoJobsPage />} />
              <Route path="/feature-tracking" element={<FeatureTrackingPage />} />
              <Route
                path="/subscriptions"
                element={
                  <div className="p-8 text-gray-900 dark:text-slate-100">
                    Subscription Plans - Coming Soon
                  </div>
                }
              />

              {/* System Section */}
              <Route path="/capabilities/:publicId" element={<CapabilityDetail />} />
              <Route path="/capabilities" element={<CapabilityManagement />} />
              <Route path="/llm-catalog" element={<LLMCatalogManagement />} />
              <Route path="/input-formats" element={<InputFormatsManagement />} />
              <Route path="/provider-formats" element={<Navigate to="/input-formats" replace />} />
              <Route path="/internal-ai-tasks/:publicId" element={<InternalAITaskDetail />} />
              <Route path="/internal-ai-tasks" element={<InternalAITaskManagement />} />
              <Route
                path="/system-characteristics/:publicId"
                element={<SystemCharacteristicDetail />}
              />
              <Route path="/system-characteristics" element={<SystemCharacteristicList />} />
              <Route path="/system-prompts" element={<SystemPromptManagement />} />
              <Route path="/system-tools" element={<ToolCallsManagementPage />} />
              <Route path="/tool-calls" element={<ToolCallsManagementPage />} />
              <Route path="/assistant-history" element={<AdminAgentHistoryPage />} />
              <Route path="/marketplace" element={<MarketplaceManagement />} />

              {/* Code Agent Section */}
              <Route path="/code-agent/sessions" element={<CodeAgentSessionsPage />} />
              <Route path="/code-agent/tools" element={<CodeAgentToolsPage />} />
              <Route path="/code-agent/audit" element={<CodeAgentAuditPage />} />
              <Route path="/code-agent/runtime" element={<CodeAgentRuntimeSettingsPage />} />
              <Route path="/code-agent/kill-switches" element={<CodeAgentKillSwitchesPage />} />

              <Route path="/ai-logs" element={<AiLogsPage />} />
              <Route path="/billing-report" element={<BillingCostReportPage />} />
              <Route
                path="/api-config"
                element={
                  <div className="p-8 text-gray-900 dark:text-slate-100">
                    API Configurations - Coming Soon
                  </div>
                }
              />
              <Route
                path="/error-monitoring"
                element={
                  <div className="p-8 text-gray-900 dark:text-slate-100">
                    Error Monitoring - Coming Soon
                  </div>
                }
              />

              {/* Settings Section */}
              <Route path="/account-settings" element={<AccountSettingsPage />} />
              <Route path="/global-settings" element={<GlobalSettings />} />
              <Route path="/anonymous-limits" element={<AnonymousLimits />} />
              <Route path="/language-settings" element={<LanguageSettings />} />
              <Route path="/agent-avatars" element={<AgentAvatarPage />} />
              <Route
                path="/prompt-templates"
                element={
                  <div className="p-8 text-gray-900 dark:text-slate-100">
                    Prompt Templates - Coming Soon
                  </div>
                }
              />

              {/* 404 Not Found */}
              <Route
                path="*"
                element={
                  <div className="p-8 text-gray-900 dark:text-slate-100">404 - Page Not Found</div>
                }
              />
            </Routes>
          </AdminLayout>
        }
      />
    </Routes>
  );
};

// ============================================
// Root App Component (with all providers)
// ============================================

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <NotificationProvider>
            <AppContent />
          </NotificationProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
